/**
 * Microsoft Graph — Bookings client.
 *
 * Wraps `/solutions/bookingBusinesses/{id}/appointments` so the
 * Appointments landing's "Bookings" tab can show customer-self-booked
 * appointments for the FSR's branch.
 * Required scope: `Bookings.Read.All` (or `Bookings.ReadWrite.All` for
 * cancel / reassign actions in Manage tab).
 *
 * Each branch has its own Bookings business id stored on the v3 Branch
 * row (`bookingsBusinessId`) — see `BookingsBusinessConfig`. Until that
 * column is seeded the client returns [] (empty zero state) so the tab
 * still renders.
 */

import type { Appointment, BookingsBusinessConfig } from '../types';
import { mockBookingsForBranch } from './mockAppointments';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

function liveEnabled(): boolean {
  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  return env['VITE_FEATURE_APPOINTMENTS_LIVE'] === 'true';
}

function mockEnabled(): boolean {
  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  return env['VITE_FEATURE_APPOINTMENTS_MOCK'] === 'true';
}

interface BookingAppointment {
  id: string;
  customerName?: string;
  customerEmailAddress?: string;
  customerPhone?: string;
  serviceName?: string;
  serviceId?: string;
  start?: { dateTime: string; timeZone: string };
  end?: { dateTime: string; timeZone: string };
  joinWebUrl?: string;
  customerNotes?: string;
  staffMemberIds?: ReadonlyArray<string>;
  isCustomerAllowedToManageBooking?: boolean;
}

interface BookingAppointmentsResponse {
  value: ReadonlyArray<BookingAppointment>;
}

function bookingToAppointment(b: BookingAppointment, branchCode: string, branchName?: string): Appointment {
  const start = b.start?.dateTime ?? new Date().toISOString();
  const end = b.end?.dateTime ?? new Date(Date.now() + 30 * 60_000).toISOString();
  const out: Appointment = {
    id: b.id,
    customerName: b.customerName ?? 'Customer',
    subject: b.serviceName ?? 'Bookings appointment',
    startUtc: start,
    endUtc: end,
    timeZone: b.start?.timeZone ?? 'UTC',
    channel: b.joinWebUrl ? 'teams' : 'bookings',
    source: 'bookings',
    branchCode,
  };
  if (branchName) out.branchName = branchName;
  if (b.serviceId) out.serviceCode = b.serviceId;
  if (b.customerNotes) out.notes = b.customerNotes;
  if (b.joinWebUrl) out.joinWebUrl = b.joinWebUrl;
  return out;
}

async function callGraph<T>(path: string, token: string): Promise<T> {
  const res = await fetch(`${GRAPH_BASE}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  if (!res.ok) {
    throw new Error(`Graph ${path} failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export interface BranchBookingsFetchOptions {
  token?: string | null;
  branchCode: string;
  branchName?: string;
  bookingsBusinessId?: string;
  /** ISO start (default = today 00:00 local). */
  startUtc?: string;
  /** ISO end (default = +7 days). */
  endUtc?: string;
}

/**
 * Fetch Bookings appointments for the given branch's Bookings business.
 *
 * Returns [] if:
 *   - feature flag off
 *   - bookingsBusinessId not configured (admin Config tab)
 *   - no token
 */
export async function fetchBranchBookings(
  opts: BranchBookingsFetchOptions,
): Promise<ReadonlyArray<Appointment>> {
  if (!liveEnabled()) {
    return mockEnabled() ? mockBookingsForBranch(opts.branchCode, opts.branchName) : [];
  }
  if (!opts.token || !opts.bookingsBusinessId) return [];

  const start = opts.startUtc ?? (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  })();
  const end = opts.endUtc ?? (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  })();

  const path =
    `/solutions/bookingBusinesses/${encodeURIComponent(opts.bookingsBusinessId)}` +
    `/appointments?$filter=start/dateTime ge '${start}' and end/dateTime le '${end}'` +
    `&$orderby=start/dateTime&$top=100`;

  try {
    const data = await callGraph<BookingAppointmentsResponse>(path, opts.token);
    return data.value.map((b) => bookingToAppointment(b, opts.branchCode, opts.branchName));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[graphBookingsClient] fetchBranchBookings failed:', err);
    return [];
  }
}

/**
 * List all Bookings businesses the signed-in user can manage.
 * Used by the Config tab to map branches → Bookings businesses.
 */
export async function listBookingsBusinesses(token: string): Promise<ReadonlyArray<{
  id: string;
  displayName: string;
  defaultCurrencyIso?: string;
  publicUrl?: string;
}>> {
  if (!liveEnabled() || !token) return [];

  try {
    const data = await callGraph<{ value: ReadonlyArray<{ id: string; displayName?: string; defaultCurrencyIso?: string; webSiteUrl?: string }> }>(
      `/solutions/bookingBusinesses?$select=id,displayName,defaultCurrencyIso,webSiteUrl`,
      token,
    );
    return data.value.map((b) => {
      const out: { id: string; displayName: string; defaultCurrencyIso?: string; publicUrl?: string } = {
        id: b.id,
        displayName: b.displayName ?? '',
      };
      if (b.defaultCurrencyIso) out.defaultCurrencyIso = b.defaultCurrencyIso;
      if (b.webSiteUrl) out.publicUrl = b.webSiteUrl;
      return out;
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[graphBookingsClient] listBookingsBusinesses failed:', err);
    return [];
  }
}

/**
 * Read the per-branch Bookings business config from local storage.
 * The Config tab mutates this; production wiring will move it to a v3
 * SQL `Branches.bookingsBusinessId` column once admin Config tab seeds it.
 */
const STORAGE_KEY = 'pcb.rae.bookingsBusinesses';

export function readLocalBookingsConfig(): ReadonlyArray<BookingsBusinessConfig> {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ReadonlyArray<BookingsBusinessConfig>;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeLocalBookingsConfig(rows: ReadonlyArray<BookingsBusinessConfig>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // ignore quota
  }
}

export function findBranchBookingsId(branchCode: string): string | undefined {
  const rows = readLocalBookingsConfig();
  return rows.find((r) => r.branchCode === branchCode)?.bookingsBusinessId;
}
